import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  TextAreaField,
} from '@leadsales/design-system/react';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import {
  LEAD_AGENT_PAYWALL_EVENTS,
  type tLeadAgentChatBannerDismissedProps,
} from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import {
  LEAD_AGENT_CHAT_BANNER_DISMISS_REASONS,
  LeadAgentChatBannerService,
  type tLeadAgentChatBannerDismissReason,
} from 'src/components/Chat/components/MessageBar/leadAgentChatBanner.service';

const COPY = 'smartLeadbot.banners.billingPaused.dismissSurvey';
const REASON_TEXT_MAX_LENGTH = 500;

export type tLeadAgentPauseDismissDialogProps = {
  open: boolean;
  workspaceId: string;
  // The reason of the pause on screen, for the event when billing's answer lacks it.
  pauseReason: string | null;
  // MLA-51's everUsed as the Owner's session knows it; null when unknown.
  everUsed: boolean | null;
  onClose: () => void;
  onDismissed: () => void;
};

function isDismissReason(value: string): value is tLeadAgentChatBannerDismissReason {
  return (LEAD_AGENT_CHAT_BANNER_DISMISS_REASONS as readonly string[]).includes(value);
}

// AGI-766 (MLA-100): the Owner closes the Lead Agent pause notice of the chat for
// the whole workspace. The question is optional: «Cerrar aviso» closes it with
// or without a reason, and «Cancelar», the X or Escape leave the notice as it is.
// Six reasons, so a select (composition.md, Forms: 6 to 15 options). While the
// close is being sent nothing closes the dialog; a failure keeps it open with
// the reason chosen, so the Owner can try again.
export default function LeadAgentPauseDismissDialog(props: tLeadAgentPauseDismissDialogProps) {
  const { t } = useTranslation('chat/chat');
  const { trackEvent } = useAnalytics();
  const [reason, setReason] = useState<tLeadAgentChatBannerDismissReason | ''>('');
  const [reasonText, setReasonText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Each opening starts over: no reason, no text, no earlier failure.
  useEffect(() => {
    if (!props.open) return;
    setReason('');
    setReasonText('');
    setFailed(false);
  }, [props.open]);

  const close = () => {
    if (!busy) props.onClose();
  };

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const text = reason === 'other' ? reasonText.trim() : '';
    const body = {
      reason: reason === '' ? null : reason,
      reasonText: text === '' ? null : text,
      everUsed: props.everUsed,
    };
    try {
      const dismissal = await LeadAgentChatBannerService.dismiss({ workspaceId: props.workspaceId, body });
      const eventProps: tLeadAgentChatBannerDismissedProps = {
        surface: 'chat',
        pause_reason: dismissal.pauseReason ?? props.pauseReason ?? 'unknown',
        reason: body.reason,
        skipped: body.reason === null,
        has_reason_text: body.reasonText !== null,
        ever_used: body.everUsed,
        days_since_trial_end: dismissal.daysSinceTrialEnd,
      };
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.CHAT_BANNER_DISMISSED, eventProps);
      setBusy(false);
      props.onDismissed();
    } catch {
      setBusy(false);
      setFailed(true);
    }
  }

  return (
    <Modal open={props.open} onClose={close} closeOnBackdrop={!busy}>
      <ModalHeader
        title={t(`${COPY}.title`)}
        description={t(`${COPY}.description`)}
        closeLabel={t(`${COPY}.close`)}
      />
      <ModalBody>
        <div className="flex flex-col gap-[var(--ls-spacing-6)]">
          <SelectField
            label={t(`${COPY}.reasonLabel`)}
            placeholder={t(`${COPY}.reasonPlaceholder`)}
            value={reason}
            disabled={busy}
            onChange={(event) => {
              const value = event.target.value;
              setReason(isDismissReason(value) ? value : '');
            }}
          >
            {LEAD_AGENT_CHAT_BANNER_DISMISS_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(`${COPY}.reasons.${value}`)}
              </option>
            ))}
          </SelectField>
          {reason === 'other' && (
            <TextAreaField
              label={t(`${COPY}.reasonTextLabel`)}
              value={reasonText}
              maxLength={REASON_TEXT_MAX_LENGTH}
              showCounter
              disabled={busy}
              onChange={(event) => setReasonText(event.target.value)}
            />
          )}
          {failed && <Alert tone="error">{t(`${COPY}.error`)}</Alert>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="ghost" onClick={close} disabled={busy}>
          {t(`${COPY}.cancel`)}
        </Button>
        {/* The loading state hides the label, so the name stays on the button itself. */}
        <Button color="primary" onClick={confirm} loading={busy} aria-label={t(`${COPY}.confirm`)}>
          {t(`${COPY}.confirm`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
